import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, Col, Descriptions, Input, Select, notification, Row, Tabs, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, SaveOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { ObtPartnerDetailResponse } from '../../../__generated__/types';
import { PortalUser } from '../../../components';
import client from '../../../config/GraphQLApolloClient';
import { MUTATION_UPDATE_MASTERPARTNERNO } from '../obtMutations';
import { QUERY_UPDATE_PARTNER_COMPANY_DETAILS } from '../obtQueries';
import ObtContactTab from './ObtContactTab';
import ObtChannelTab from './ObtChannelTab';
import ObtLegalTab from './ObtLegalTab';
import ObtSurveyDetailsTab from './ObtSurveyDetailsTab';
import { ObtAccessRights, getObtAccessRights } from '../obtEntitlements';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PartnerIdentity {
    partnerno: string;
    companycode: string;
    category: string;
    systemCode: string;
    partnerName: string;
    idpartner: number;
}

type ObtPartnerDetailsTabProps = {
    partnerIdentity: PartnerIdentity;
    partnerDetails: ObtPartnerDetailResponse;
    currentUser: PortalUser;
    distinctEntitlements?: string[];
    obtAccessRights?: ObtAccessRights;
    onBackToSearch: () => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

const ObtPartnerDetailsTab: React.FC<ObtPartnerDetailsTabProps> = ({
    partnerIdentity,
    partnerDetails,
    currentUser: _currentUser,
    distinctEntitlements,
    obtAccessRights: injectedObtAccessRights,
    onBackToSearch,
}) => {
    const intl = useIntl();
    const { partnerMain, partnerCompany, partnerAddress, partnerVatcode } = partnerDetails;
    const obtAccessRights = useMemo(
        () => injectedObtAccessRights ?? getObtAccessRights(distinctEntitlements ?? []),
        [distinctEntitlements, injectedObtAccessRights]
    );
    const canUpdatePartner = obtAccessRights.canMaintainPartner;

    // ─── Editable state: Master Partner No ────────────────────────────────────
    const [masterPartnerNo, setMasterPartnerNo] = useState(partnerMain?.masterpartnerno ?? '');
    const [savingMaster, setSavingMaster] = useState(false);
    const [masterSaved, setMasterSaved] = useState(false);

    // ─── Editable state: Remarks ──────────────────────────────────────────────
    const [remarks, setRemarks] = useState(partnerCompany?.remark ?? '');
    const [savingRemarks, setSavingRemarks] = useState(false);
    const [remarksSaved, setRemarksSaved] = useState(false);

    // ─── Save Master Partner No ───────────────────────────────────────────────
    const handleSaveMasterPartnerNo = useCallback(async () => {
        setSavingMaster(true);
        setMasterSaved(false);
        try {
            await client.mutate({
                mutation: MUTATION_UPDATE_MASTERPARTNERNO,
                variables: {
                    masterpartnerno: masterPartnerNo,
                    partnerno: partnerIdentity.partnerno,
                    idpartner: partnerIdentity.idpartner,
                },
            });
            setMasterSaved(true);
            notification.success({
                message: intl.formatMessage({ id: 'obt.details.masterPartnerNo.saved', defaultMessage: 'Master Partner No updated.' }),
            });
        } catch (err) {
            console.error('Failed to save Master Partner No:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.details.masterPartnerNo.error', defaultMessage: 'Failed to update Master Partner No.' }),
            });
        } finally {
            setSavingMaster(false);
        }
    }, [masterPartnerNo, partnerIdentity, intl]);

    // ─── Save Remarks ─────────────────────────────────────────────────────────
    const handleSaveRemarks = useCallback(async () => {
        setSavingRemarks(true);
        setRemarksSaved(false);
        try {
            await client.mutate({
                mutation: QUERY_UPDATE_PARTNER_COMPANY_DETAILS,
                variables: {
                    partnerCompany: {
                        id: partnerCompany?.id,
                        partnerno: partnerIdentity.partnerno,
                        idpartner: partnerIdentity.idpartner,
                        companycode: partnerIdentity.companycode,
                        remark: remarks,
                    },
                },
            });
            setRemarksSaved(true);
            notification.success({
                message: intl.formatMessage({ id: 'obt.details.remarks.saved', defaultMessage: 'Comments saved.' }),
            });
        } catch (err) {
            console.error('Failed to save remarks:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.details.remarks.error', defaultMessage: 'Failed to save comments.' }),
            });
        } finally {
            setSavingRemarks(false);
        }
    }, [remarks, partnerCompany, partnerIdentity, intl]);

    // ─── VAT display ──────────────────────────────────────────────────────────
    const vatOptions = Array.from(
        new Set(
            (partnerVatcode ?? [])
                .map((v) => `${v.country ?? ''} / ${v.vatcode ?? ''}`.trim())
                .filter((value) => value !== '/' && value.length > 0)
        )
    ).map((value) => ({ label: value, value }));
    const vatSelectedValue = vatOptions[0]?.value;

    // ─── Sub-tabs ─────────────────────────────────────────────────────────────
    const subTabItems = [
        {
            key: 'partnerAndAddress',
            label: intl.formatMessage({ id: 'obt.details.tab.partnerAndAddress', defaultMessage: 'Partner & Address Details' }),
            children: (
                <Row gutter={[16, 16]}>
                    {/* Card 1 — Partner Details */}
                    <Col xs={24} lg={12} className="obt-details-card-col">
                        <Card
                            className="obt-details-card"
                            title={intl.formatMessage({ id: 'obt.details.card.partner', defaultMessage: 'Partner Details' })}
                            size="small"
                        >
                            <Descriptions column={1} size="small" bordered={true} labelStyle={{ width: 180, fontWeight: 500 }}>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.partnerNo', defaultMessage: 'Partner No' })}>
                                    {partnerMain?.partnerno ?? partnerIdentity.partnerno ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.masterPartnerNo', defaultMessage: 'Master ID' })}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Input
                                            value={masterPartnerNo}
                                            onChange={(e) => { setMasterPartnerNo(e.target.value); setMasterSaved(false); }}
                                            style={{ maxWidth: 180 }}
                                            size="small"
                                            disabled={!canUpdatePartner}
                                        />
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={masterSaved ? <CheckOutlined /> : <SaveOutlined />}
                                            loading={savingMaster}
                                            onClick={handleSaveMasterPartnerNo}
                                            disabled={!canUpdatePartner}
                                        >
                                            {intl.formatMessage({ id: 'obt.details.updateMasterPartnerNo', defaultMessage: 'Update' })}
                                        </Button>
                                    </div>
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.buno', defaultMessage: 'Buno' })}>
                                    {partnerCompany?.cbuno ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.category', defaultMessage: 'Category' })}>
                                    {partnerMain?.category ?? partnerIdentity.category ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.language', defaultMessage: 'Language' })}>
                                    {partnerAddress?.language ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.system', defaultMessage: 'System' })}>
                                    {partnerMain?.systemid ?? partnerIdentity.systemCode ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.aparLink', defaultMessage: 'AP/AR Link' })}>
                                    {partnerCompany?.aparpartnerlink ?? '—'}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    {/* Card 2 — Company Details */}
                    <Col xs={24} lg={12} className="obt-details-card-col">
                        <Card
                            className="obt-details-card"
                            title={intl.formatMessage({ id: 'obt.details.card.company', defaultMessage: 'Company Details' })}
                            size="small"
                        >
                            <Descriptions column={1} size="small" bordered={true} labelStyle={{ width: 180, fontWeight: 500 }}>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.vatCode', defaultMessage: 'Country / VAT Code' })}>
                                    {vatOptions.length > 0 ? (
                                        <Select
                                            value={vatSelectedValue}
                                            options={vatOptions}
                                            disabled={false}
                                            style={{ width: '100%' }}
                                            size="small"
                                            showSearch={false}
                                        />
                                    ) : (
                                        '—'
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.taxId', defaultMessage: 'Tax ID' })}>
                                    {partnerMain?.taxId ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.phone', defaultMessage: 'Phone' })}>
                                    {partnerAddress?.phone ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.companyCode', defaultMessage: 'Company Code' })}>
                                    {partnerCompany?.companycode ?? partnerIdentity.companycode ?? '—'}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    {/* Card 3 — Address */}
                    <Col xs={24} lg={12} className="obt-details-card-col">
                        <Card
                            className="obt-details-card"
                            title={intl.formatMessage({ id: 'obt.details.card.address', defaultMessage: 'Address' })}
                            size="small"
                        >
                            <Descriptions column={1} size="small" bordered={true} labelStyle={{ width: 180, fontWeight: 500 }}>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.companyName', defaultMessage: 'Company Name' })}>
                                    {partnerAddress?.partnername ?? partnerIdentity.partnerName ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.street', defaultMessage: 'Street' })}>
                                    {partnerAddress?.street1 ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.city', defaultMessage: 'City' })}>
                                    {partnerAddress?.city ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.poBox', defaultMessage: 'P.O. Box' })}>
                                    {partnerAddress?.pobox ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.postalCode', defaultMessage: 'Postal Code' })}>
                                    {partnerAddress?.postalcode ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.region', defaultMessage: 'State / Region' })}>
                                    {partnerAddress?.region ?? '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label={intl.formatMessage({ id: 'obt.details.country', defaultMessage: 'Country' })}>
                                    {partnerAddress?.countrycode ?? '—'}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    {/* Card 4 — Comments */}
                    <Col xs={24} lg={12} className="obt-details-card-col">
                        <Card
                            className="obt-details-card"
                            title={intl.formatMessage({ id: 'obt.details.card.comments', defaultMessage: 'Comments' })}
                            size="small"
                        >
                            <Input.TextArea
                                rows={6}
                                value={remarks}
                                onChange={(e) => { setRemarks(e.target.value); setRemarksSaved(false); }}
                                placeholder={intl.formatMessage({ id: 'obt.details.remarks.placeholder', defaultMessage: 'Enter comments…' })}
                                style={{ marginBottom: 12 }}
                                disabled={!canUpdatePartner}
                            />
                            <Button
                                type="primary"
                                icon={remarksSaved ? <CheckOutlined /> : <SaveOutlined />}
                                loading={savingRemarks}
                                onClick={handleSaveRemarks}
                                disabled={!canUpdatePartner}
                            >
                                {intl.formatMessage({ id: 'obt.details.saveComments', defaultMessage: 'Save Comments' })}
                            </Button>
                        </Card>
                    </Col>
                </Row>
            ),
        },
        {
            key: 'contacts',
            label: intl.formatMessage({ id: 'obt.details.tab.contacts', defaultMessage: 'Contacts' }),
            children: (
                <ObtContactTab
                    partnerIdentity={partnerIdentity}
                    partnerContact={partnerDetails.partnerContact ?? []}
                    idCompany={partnerCompany?.id ?? undefined}
                    canUpdate={obtAccessRights.canMaintainContacts}
                />
            ),
        },
        {
            key: 'channels',
            label: intl.formatMessage({ id: 'obt.details.tab.channels', defaultMessage: 'Channels' }),
            children: (
                <ObtChannelTab
                    partnerIdentity={partnerIdentity}
                    partnerChannel={partnerDetails.partnerChannel ?? []}
                    idCompany={partnerCompany?.id ?? undefined}
                    canUpdate={obtAccessRights.canMaintainChannels}
                />
            ),
        },
        {
            key: 'legal',
            label: intl.formatMessage({ id: 'obt.details.tab.legal', defaultMessage: 'Legal' }),
            children: (
                <ObtLegalTab
                    partnerLegalData={partnerDetails.partnerLegal ?? []}
                    partnerno={partnerIdentity.partnerno}
                    companycode={partnerIdentity.companycode}
                    idpartner={partnerIdentity.idpartner}
                    country={partnerAddress?.countrycode ?? ''}
                    canUpdate={obtAccessRights.canMaintainLegal}
                />
            ),
        },
        {
            key: 'survey',
            label: intl.formatMessage({ id: 'obt.details.tab.survey', defaultMessage: 'Survey Details' }),
            children: (
                <ObtSurveyDetailsTab partnerIdentity={partnerIdentity} />
            ),
        },
    ];

    return (
        <div className="obt-partner-details">
            {/* ─── Header bar ──────────────────────────────────────────────────── */}
            <div className="obt-details-header">
                <Tooltip title={intl.formatMessage({ id: 'obt.details.backToSearch', defaultMessage: 'Back to Search' })}>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBackToSearch}
                        size="large"
                        data-testid="obt-back-to-search"
                    >
                        {intl.formatMessage({ id: 'obt.details.backToSearch', defaultMessage: 'Back to Search' })}
                    </Button>
                </Tooltip>
                <div className="obt-details-identity">
                    <Tag color="blue">{partnerIdentity.systemCode}</Tag>
                    <Tag color="green">{partnerIdentity.category}</Tag>
                    <span className="obt-details-identity-text">
                        {partnerIdentity.partnerno} — {partnerIdentity.companycode}
                    </span>
                    <span className="obt-details-identity-name">
                        {partnerIdentity.partnerName}
                    </span>
                </div>
            </div>

            {/* ─── Sub-tabs ────────────────────────────────────────────────────── */}
            <Tabs
                defaultActiveKey="partnerAndAddress"
                items={subTabItems}
                size="middle"
                style={{ marginTop: 8 }}
            />
        </div>
    );
};

export default ObtPartnerDetailsTab;
